import React, { FC, useCallback, useState } from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Divider,
    Flex,
    Heading,
    Link,
    LinkProps,
    Text
} from '@chakra-ui/react';
import { ReactComponent as CheckSuccessIcon } from 'svg/check-success.svg';
import CircleIcon from 'ts/common/components/gallery/CircleIcon';
import { BundleFileIcon, DownloadIcon, SlideshowIcon } from 'ts/common/components/icons';

const HelpfulTipsButton: FC<ButtonProps & LinkProps> = ({ children, ...rest }) => (
    <Button
        as={Link}
        target="_blank"
        rel="noopener noreferrer"
        borderColor="gray.400"
        color="text"
        fontWeight="semibold"
        variant="outline"
        width={{ base: 'full', lg: '358px' }}
        _active={{
            backgroundColor: 'initial'
        }}
        _focus={{
            boxShadow: 'none'
        }}
        _focusVisible={{
            boxShadow: 'outline'
        }}
        _hover={{
            backgroundColor: 'initial',
            textDecoration: 'none'
        }}
        _visited={{
            color: 'text'
        }}
        {...rest}
    >
        {children}
    </Button>
);

export interface IBundleFile {
    filename: string;
    size: string;
    /** Always present on the response; the consuming controllers navigate to it. */
    url: string;
}

export interface IBundleVideo {
    filename: string;
    url: string;
}

export type IBundleSlideshow = IBundleVideo;

/** A slideshow is delivered exactly like a video, so `IBundleSlideshow` adds nothing here. */
export type IBundleItem = IBundleFile | IBundleVideo;

/**
 * A bought slideshow with no download link yet. 'failed' is terminal: that render will never
 * become a link, so it must not read as work in progress.
 */
export interface IBundlePendingSlideshow {
    photoId: number;
    filename: string;
    status: 'processing' | 'failed';
}

export const getFileLabel = (file: IBundleFile) => `${file.filename} (${file.size})`;

interface Props {
    /** A list of files to be rendered. Absent while the bundle is not ready yet. */
    files?: IBundleFile[];
    /** A list of videos to be rendered. Videos are not zipped; they are direct download links. */
    videos?: IBundleVideo[];
    /** A list of slideshows to be rendered. Slideshows are not zipped either; their exported
     *  video is a direct download link, same as a video. */
    slideshows?: IBundleSlideshow[];
    /** Slideshows still rendering, or whose render failed. Listed, but nothing to download. */
    pendingSlideshows?: IBundlePendingSlideshow[];
    /**
     * Turns on the Download / Download Again affordance, which this component then tracks
     * itself. Off by default, so a list that only links out renders as it always has.
     */
    showDownloadState?: boolean;
    /** A function to be called when a file, video, or slideshow is clicked */
    onClickFile: (file: IBundleItem) => unknown;
    /** The Angular translation service */
    translateFilter: SpAngularJs.ITranslateFilter;
}

interface ItemProps {
    item: IBundleItem;
    index: number;
    label: string;
    icon: typeof BundleFileIcon;
    size?: string;
}

const BundleFileList: FC<Props> = ({
    files = [],
    videos = [],
    slideshows = [],
    pendingSlideshows = [],
    showDownloadState = false,
    onClickFile,
    translateFilter: translate
}) => {
    // Tracked by object identity rather than by filename: two videos in one bundle can share
    // a filename, so a name-keyed set would mark both as downloaded.
    const [downloadedItems, setDownloadedItems] = useState<IBundleItem[]>([]);
    const handleClickItem = useCallback(
        (item: IBundleItem) => {
            setDownloadedItems((items) => (items.includes(item) ? items : [...items, item]));
            onClickFile(item);
        },
        [onClickFile]
    );

    // A 1-D list (icon on the left, name/size stacked to its right) rather
    // than a grid of icon-above-name columns - a long name now has the
    // item's full width to wrap into instead of being squeezed into (or
    // overflowing past) a narrow fixed-width column, and doesn't need its
    // own centering since the whole row is left-aligned.
    const renderItem = ({ item, index, label, icon, size }: ItemProps) => {
        const isDownloaded = downloadedItems.includes(item);
        const actionLabel =
            showDownloadState && translate(isDownloaded ? 'downloadAgain' : 'download');

        // A video bought alone and via "all videos" at one resolution comes back twice with one
        // URL, so the index keeps the key unique.
        return (
            <Button
                key={`${item.url}-${index}`}
                onClick={() => handleClickItem(item)}
                aria-label={actionLabel ? `${label}, ${actionLabel}` : label}
                alignItems="center"
                borderRadius="0px"
                borderStyle="none"
                color="brand.primary"
                display="flex"
                gap="16px"
                height="auto"
                justifyContent="flex-start"
                textDecoration="underline"
                variant="link"
                whiteSpace="normal"
                width="full"
                _focus={{
                    boxShadow: 'none'
                }}
                _focusVisible={{
                    boxShadow: 'outline'
                }}
            >
                <CircleIcon icon={icon} size="lg" />
                <Flex direction="column" textAlign="left">
                    <Text
                        color="brand.primary"
                        fontSize="md"
                        fontWeight="semibold"
                        letterSpacing="0px"
                        overflowWrap="break-word"
                        title={item.filename}
                    >
                        {item.filename}
                    </Text>
                    {size && <Text color="brand.primary">{size}</Text>}
                    {actionLabel && (
                        <Flex alignItems="center" gap="6px" marginTop="4px">
                            {/* aria-hidden is required: the state is already in the accessible
                            name above, and Chakra's Icon does not add it for us. */}
                            {isDownloaded && (
                                <CheckSuccessIcon aria-hidden height="14px" width="14px" />
                            )}
                            <Text
                                color="brand.primary"
                                fontSize="sm"
                                fontWeight="semibold"
                                marginBottom="0"
                            >
                                {actionLabel}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            </Button>
        );
    };

    return (
        <Flex alignItems="center" flexDirection="column">
            <Flex direction="column" gap="24px" width={{ base: 'full', lg: '358px' }}>
                {files.map((file, index) =>
                    renderItem({
                        item: file,
                        index,
                        label: getFileLabel(file),
                        icon: BundleFileIcon,
                        size: file.size
                    })
                )}
                {videos.map((video, index) =>
                    renderItem({ item: video, index, label: video.filename, icon: DownloadIcon })
                )}
                {slideshows.map((slideshow, index) =>
                    renderItem({
                        item: slideshow,
                        index,
                        label: slideshow.filename,
                        icon: SlideshowIcon
                    })
                )}
                {/* Plain rows, not buttons: there is nothing to download yet, and a control
                    that does nothing is worse than none. Keyed by position as well as photo id:
                    the list has one entry per sale, so one slideshow bought at two tiers
                    appears twice, and two slideshows in one bundle can share a filename. */}
                {pendingSlideshows.map((pending, index) => (
                    <Flex
                        key={`${pending.photoId}-${index}`}
                        alignItems="center"
                        gap="16px"
                        width="full"
                    >
                        <CircleIcon icon={SlideshowIcon} size="lg" />
                        <Flex direction="column" textAlign="left">
                            <Text
                                fontSize="md"
                                fontWeight="semibold"
                                letterSpacing="0px"
                                marginBottom="0"
                                overflowWrap="break-word"
                                title={pending.filename}
                            >
                                {pending.filename}
                            </Text>
                            <Text fontSize="sm" marginBottom="0" marginTop="4px">
                                {translate(
                                    pending.status === 'failed'
                                        ? 'slideshowRenderFailed'
                                        : 'slideshowRenderProcessing'
                                )}
                            </Text>
                        </Flex>
                    </Flex>
                ))}
            </Flex>
            {/* Every tip here is about opening a .zip, and only `files` are delivered as one -
                videos and slideshows are direct download links. A bundle with no zip parts has
                nothing to unzip, so the whole section drops rather than offering the viewer
                instructions for a file they were never given. */}
            {files.length > 0 && (
                <>
                    <Divider marginY="32px" />
                    <Box marginBottom="24px">
                        {/* h2, not h3: both consuming pages render an h1 and no h2, so h3
                            would skip a level. `size` keeps the visual size unchanged. */}
                        <Heading as="h2" marginY="16px" size="md" variant="primary">
                            {translate('zipBundleHelpfulTips')}
                        </Heading>
                        <Text fontSize="md" marginY="24px">
                            {translate('zipBundleHelpfulTipsItem1')}
                        </Text>
                        <Text fontSize="md" marginY="24px">
                            {translate('zipBundleHelpfulTipsItem2')}
                        </Text>
                    </Box>
                    <Flex alignItems="center" alignSelf="stretch" flexDirection="column" gap="16px">
                        <HelpfulTipsButton href="https://support.apple.com/kb/PH25411">
                            macOS
                        </HelpfulTipsButton>
                        <HelpfulTipsButton href="https://support.microsoft.com/help/14200/windows-compress-uncompress-zip-files">
                            Windows
                        </HelpfulTipsButton>
                    </Flex>
                </>
            )}
        </Flex>
    );
};

export default BundleFileList;
